// indent.ts - net-new (no cloud/playground precedent) CM6 `indentService` for
// dbt charts YAML, translating the intent of the vscode YAML
// `indentationRules.increaseIndentPattern`
// (apps/vscode-extension/language-configuration.json) into CM6's
// column-based indent API.
//
// CM6's `indentService` returns a target *column* for a line, not text to
// insert, so the vscode `onEnterRules` "appendText: '- '" behavior cannot be
// expressed through it. It lives here instead as a plain Enter command
// (`dbtChartsListContinuation`), which is the only place text - rather than a
// column - can be inserted.

import { type IndentContext, indentService } from "@codemirror/language";
import { EditorSelection, type EditorState, type Extension } from "@codemirror/state";
import { keymap, type Command, type EditorView } from "@codemirror/view";

// Mirrors vscode's `increaseIndentPattern`: a line ending (trailing
// whitespace aside) in a mapping colon, a lone sequence dash, or a `|`/`>`
// block-scalar header (with optional `-`/`+` chomping indicator) opens a
// nested indent level. A populated sequence item ("- foo") does not match -
// its trailing content isn't one of these markers - so it maintains the
// current level, matching vscode.
export const INCREASES_INDENT_RE = /(:|-|\|[-+]?|>[-+]?)\s*$/;

/**
 * Whether `text` opens a nested indent level. The one answer to that question,
 * shared with `format.ts` so the formatter and the indent service can never
 * disagree about the same line.
 *
 * The pattern anchors at end of line, so a trailing comment hides the marker
 * that precedes it (`revenue: # sql to a database` reads as ending in `e`).
 * Callers that can tell a real comment from a `#` inside a quoted value - the
 * formatter, which has the parse tree - pass the line already cut at the
 * comment. The indent service has no tree here and passes the line as typed:
 * it errs toward *not* opening a level, which costs one Enter's indentation
 * rather than restructuring a document.
 */
export function opensIndent(text: string): boolean {
  return INCREASES_INDENT_RE.test(text);
}

/** A populated sequence item: a dash with content after it on the same line. */
const POPULATED_ITEM_RE = /^([ \t]*)-([ \t]+)\S/;

/** An item that starts a mapping (`- name: a`): more keys of that object usually follow. */
const MAPPING_ITEM_RE = /^[ \t]*-[ \t]+[^\s#'"][^:#]*:([ \t]|$)/;

/** The column an item's content starts at: the indent before its dash, the dash, and the spacing after it. */
function itemContentColumn(item: RegExpExecArray): number {
  return item[1].length + 1 + item[2].length;
}

/**
 * The dash column of an item that starts a mapping (`- name: a`), else null.
 * Enter after such a line indents to its keys; a pasted sibling item belongs
 * at this column instead (`format.ts`).
 */
export function mappingItemDashColumn(text: string): number | null {
  return MAPPING_ITEM_RE.test(text) ? leadingWidth(text) : null;
}

function leadingWidth(text: string): number {
  return text.length - text.trimStart().length;
}

/**
 * Net-new CM6 indent service for dbt charts YAML: after a line that opens a
 * nested level (mapping key, lone sequence dash, block-scalar header),
 * indent by one more unit than that line; after an item that starts a
 * mapping (`- name: a`), indent to that mapping's key column; otherwise
 * maintain the previous non-blank line's indent. Uses `context.unit` for the
 * step size - never a hardcoded column count - so it respects the editor's
 * configured `indentUnit`.
 *
 * `insertNewlineAndIndent` asks before the break exists (`simulatedBreak`):
 * the text before `pos` is then the line above the one being indented.
 */
export function dbtChartsIndentService(): Extension {
  return indentService.of((context: IndentContext, pos: number): number | null => {
    const doc = context.state.doc;
    const line = doc.lineAt(pos);
    const indentAfter = (text: string, indent: number): number => {
      if (INCREASES_INDENT_RE.test(text)) return indent + context.unit;
      const item = MAPPING_ITEM_RE.test(text) ? POPULATED_ITEM_RE.exec(text) : null;
      return item ? itemContentColumn(item) : indent;
    };
    if (context.simulatedBreak === pos) {
      const before = doc.sliceString(line.from, pos);
      if (before.trim() !== "") return indentAfter(before, context.lineIndent(line.from, -1));
    }
    for (let n = line.number - 1; n >= 1; n -= 1) {
      const prev = doc.line(n);
      if (prev.text.trim() !== "") return indentAfter(prev.text, context.lineIndent(prev.from));
    }
    return 0;
  });
}

/**
 * The sequence item a blank line at `width` columns sits inside, or null:
 * an item whose content starts at `width`, or whose dash is at `width` with
 * no key at `width` in between (in a compact list such a key ends the list).
 */
function enclosingItem(state: EditorState, lineNumber: number, width: number): RegExpExecArray | null {
  let crossedKey = false;
  for (let n = lineNumber - 1; n >= 1; n -= 1) {
    const text = state.doc.line(n).text;
    if (text.trim() === "") continue;
    const indent = leadingWidth(text);
    if (indent > width) continue;
    const item = POPULATED_ITEM_RE.exec(text);
    if (indent === width) {
      if (item) return crossedKey ? null : item;
      crossedKey = true;
      continue;
    }
    return item && itemContentColumn(item) === width ? item : null;
  }
  return null;
}

/**
 * Whether the blank line at `lineNumber` is not the end of the item whose dash
 * sits at `dash`: the item's own keys follow, or (when the blank line is at the
 * dash column) a key at that column shows the compact list already ended.
 */
function itemContinuesBelow(state: EditorState, lineNumber: number, dash: number, width: number): boolean {
  for (let n = lineNumber + 1; n <= state.doc.lines; n += 1) {
    const text = state.doc.line(n).text;
    if (text.trim() === "") continue;
    const indent = leadingWidth(text);
    return indent > dash || (width === dash && indent === dash && !/^[ \t]*-(\s|$)/.test(text));
  }
  return false;
}

/**
 * Enter starts a new sequence item (`- `) only where nothing else can follow:
 *
 * - at the end of a finished scalar item (`- foo`), or
 * - on a blank line at the end of an item, which the author left to separate
 *   items. The blank line stays as the separator, emptied of whitespace. A
 *   blank line with the item's own keys below it is not its end.
 *
 * Enter after an item that starts a mapping (`- name: a`) does not continue:
 * the author is usually still typing that object, and the indent service puts
 * the cursor at its key column. Neither does an item that opens a block
 * (`- cols:`, `- text: |`) or a bare `- `. A selection, multiple cursors, or
 * a cursor mid-line fall through to the ordinary newline command.
 */
export const dbtChartsListContinuation: Command = (view: EditorView): boolean => {
  const { state } = view;
  // CM6 runs keymap handlers in a read-only view (that is how search keys work
  // there), so a mutating command has to say no itself.
  if (state.readOnly) return false;
  const range = state.selection.main;
  if (state.selection.ranges.length !== 1 || !range.empty) return false;
  const line = state.doc.lineAt(range.head);
  if (line.text.trim() === "") {
    const item = enclosingItem(state, line.number, line.length);
    if (!item || itemContinuesBelow(state, line.number, item[1].length, line.length)) return false;
    const insert = `\n${item[1]}-${item[2]}`;
    view.dispatch({
      changes: { from: line.from, to: line.to, insert },
      selection: EditorSelection.cursor(line.from + insert.length),
      userEvent: "input",
      scrollIntoView: true,
    });
    return true;
  }
  if (range.head !== line.to) return false;
  if (INCREASES_INDENT_RE.test(line.text) || MAPPING_ITEM_RE.test(line.text)) return false;
  const item = POPULATED_ITEM_RE.exec(line.text);
  if (!item) return false;
  // The new item copies the current one's own leading whitespace and dash
  // spacing verbatim: a sequence's items are siblings, so whatever column and
  // spacing this one uses is by definition what the next one needs.
  const insert = `\n${item[1]}-${item[2]}`;
  view.dispatch({
    changes: { from: range.head, insert },
    selection: EditorSelection.cursor(range.head + insert.length),
    userEvent: "input",
    scrollIntoView: true,
  });
  return true;
};

/**
 * Enter with the cursor in front of a line's content pushes the line down
 * untouched. `insertNewlineAndIndent` would replace its leading whitespace
 * with whatever the indent service computes from the line above, silently
 * re-nesting a line that was already where the author put it.
 */
export const dbtChartsNewlineBeforeContent: Command = (view: EditorView): boolean => {
  const { state } = view;
  if (state.readOnly) return false;
  const range = state.selection.main;
  if (state.selection.ranges.length !== 1 || !range.empty) return false;
  const line = state.doc.lineAt(range.head);
  if (line.text.trim() === "") return false;
  if (range.head - line.from > leadingWidth(line.text)) return false;
  view.dispatch({
    changes: { from: line.from, insert: "\n" },
    selection: EditorSelection.cursor(range.head + 1),
    userEvent: "input",
    scrollIntoView: true,
  });
  return true;
};

/**
 * `dbtChartsNewlineBeforeContent` and `dbtChartsListContinuation` bound to
 * Enter, at a precedence the caller keeps:
 * it must sit below the completion package's fresh-line Enter (`Prec.highest`)
 * and above `defaultKeymap`'s `insertNewlineAndIndent`. Ships with the YAML
 * language, never in `dbtChartsKeymap()` - `- ` continuation in a SQL editor
 * would be nonsense.
 */
export function dbtChartsIndentKeymap(): Extension {
  return keymap.of([
    { key: "Enter", run: dbtChartsNewlineBeforeContent },
    { key: "Enter", run: dbtChartsListContinuation },
  ]);
}
